import type {BlogPost} from '@/apiTypes/blog';
import {getProfileName} from '@/utils/getProfileName';

type PersonAuthor = {
  '@type': 'Person';
  name: string;
  url: string;
};

type OrganizationAuthor = {
  '@type': 'Organization';
  name: string;
  url: string;
};

type Publisher = {
  '@type': 'Organization';
  name: string;
  logo: {
    '@type': 'ImageObject';
    url: string;
  };
};

export type BlogPostingSchema = {
  '@context': 'https://schema.org';
  '@type': 'BlogPosting';
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  mainEntityOfPage: string;
  author: PersonAuthor | OrganizationAuthor;
  publisher: Publisher;
  keywords?: string[];
};

/**
 * Builds a Google-valid BlogPosting JSON-LD object from a blog post.
 * Falls back to the OG image when no hero image is set, and uses Wayra
 * as the author when no profile is attached.
 */
export function buildBlogPostingSchema(
  post: BlogPost,
  siteUrl: string,
): BlogPostingSchema {
  const publishedAt = post.publishedAt ?? post.createdAt;

  const author: PersonAuthor | OrganizationAuthor = post.author
    ? {
        '@type': 'Person',
        name: getProfileName(post.author, 'Wayra'),
        url: `${siteUrl}/rider/${post.author.id}`,
      }
    : {
        '@type': 'Organization',
        name: 'Wayra',
        url: siteUrl,
      };

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.heroImageUrl ?? `${siteUrl}/api/og/blog/${post.id}`,
    datePublished: publishedAt.toISOString(),
    dateModified: post.updatedAt.toISOString(),
    mainEntityOfPage: `${siteUrl}/blog/${post.slug}`,
    author,
    publisher: {
      '@type': 'Organization',
      name: 'Wayra',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo_beta.svg`,
      },
    },
    keywords: post.tags.length > 0 ? post.tags.map((t) => t.name) : undefined,
  };
}
